import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Award, Download, ExternalLink, Search, Trophy, ShieldCheck, TrendingUp,
  Eye, EyeOff, Plus, Upload, Image as ImageIcon, X, Trash2,
} from 'lucide-react';
import { adminAPI, fmtError } from '@/lib/api';
import { formatMoney } from '@/lib/format';

const TYPE_META = {
  payout:         { label: 'Performance Reward', icon: Trophy,     tint: '#00ff66' },
  phase_1_passed: { label: 'Phase 1 Passed',     icon: ShieldCheck,tint: '#33ff85' },
  phase_2_passed: { label: 'Phase 2 Passed',     icon: ShieldCheck,tint: '#33ff85' },
  funded:         { label: 'Performance Trader', icon: TrendingUp, tint: '#00ff66' },
};

const FILTERS = [
  { id: 'all',            label: 'All' },
  { id: 'payout',         label: 'Payout' },
  { id: 'funded',         label: 'Funded' },
  { id: 'phase_1_passed', label: 'Phase 1' },
  { id: 'phase_2_passed', label: 'Phase 2' },
];

/** Convert a File → data-URL string. */
function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Failed to read file'));
    r.readAsDataURL(file);
  });
}

const MAX_UPLOAD_BYTES = 6 * 1024 * 1024;

export default function AdminCertificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [showcaseModal, setShowcaseModal] = useState(false);

  const load = () => {
    setLoading(true);
    setErr('');
    adminAPI.certificates({ cert_type: filter })
      .then(list => setCerts(Array.isArray(list) ? list : []))
      .catch(e => setErr(fmtError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const filtered = useMemo(() => {
    if (!q.trim()) return certs;
    const needle = q.trim().toLowerCase();
    return certs.filter(c =>
      (c.user_email || '').toLowerCase().includes(needle)
      || (c.user_name || '').toLowerCase().includes(needle)
      || (c.trader_name || '').toLowerCase().includes(needle)
      || (c.certificate_id || '').toLowerCase().includes(needle)
      || (c.plan_name || '').toLowerCase().includes(needle),
    );
  }, [certs, q]);

  const counts = useMemo(() => {
    const c = { all: certs.length, payout: 0, funded: 0, phase_1_passed: 0, phase_2_passed: 0 };
    certs.forEach(x => { if (x.cert_type in c) c[x.cert_type] += 1; });
    return c;
  }, [certs]);

  const download = async (id) => {
    setDownloading(id);
    try { await adminAPI.downloadCertificate(id); }
    catch (e) { setErr(fmtError(e)); }
    finally { setDownloading(null); }
  };

  const togglePublic = async (cert) => {
    const nextPublic = !cert.is_public;
    setCerts(prev => prev.map(c => c.certificate_id === cert.certificate_id ? { ...c, is_public: nextPublic } : c));
    try { await adminAPI.updateCertificate(cert.certificate_id, { is_public: nextPublic }); }
    catch (e) {
      setErr(fmtError(e));
      setCerts(prev => prev.map(c => c.certificate_id === cert.certificate_id ? { ...c, is_public: !nextPublic } : c));
    }
  };

  const uploadImage = async (cert, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Only image files are supported.'); return; }
    if (file.size > MAX_UPLOAD_BYTES) { setErr('Image too large — max 6 MB.'); return; }
    setUploadingId(cert.certificate_id);
    setErr('');
    try {
      const dataUrl = await readFileAsDataURL(file);
      await adminAPI.updateCertificate(cert.certificate_id, { image_base64: dataUrl });
      setCerts(prev => prev.map(c => c.certificate_id === cert.certificate_id ? { ...c, has_image: true } : c));
    } catch (e) { setErr(fmtError(e)); }
    finally { setUploadingId(null); }
  };

  const clearImage = async (cert) => {
    if (!window.confirm('Remove the uploaded image from this certificate?')) return;
    setUploadingId(cert.certificate_id);
    try {
      await adminAPI.updateCertificate(cert.certificate_id, { image_base64: '' });
      setCerts(prev => prev.map(c => c.certificate_id === cert.certificate_id ? { ...c, has_image: false } : c));
    } catch (e) { setErr(fmtError(e)); }
    finally { setUploadingId(null); }
  };

  const deleteShowcase = async (cert) => {
    if (!window.confirm(`Delete showcase certificate for "${cert.trader_name}"?`)) return;
    try {
      await adminAPI.deleteCertificate(cert.certificate_id);
      setCerts(prev => prev.filter(c => c.certificate_id !== cert.certificate_id));
    } catch (e) { setErr(fmtError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-certificates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-sm mb-2">Certificates</div>
          <h1 className="font-display text-3xl">Issued Certificates</h1>
          <p className="text-zinc-500 text-sm mt-1">View, download, upload images and curate the Wall of Consistency.</p>
        </div>
        <div className="flex items-end gap-6">
          <div className="text-right">
            <div className="label-xs">TOTAL ISSUED</div>
            <div className="font-display text-3xl text-[#00ff66]" data-testid="cert-total-count">{certs.length}</div>
          </div>
          <button
            className="btn-primary"
            onClick={() => setShowcaseModal(true)}
            data-testid="new-showcase-cert-btn"
          >
            <Plus size={14} className="inline mr-1" /> Add showcase certificate
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`cert-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition border
              ${filter === f.id
                ? 'bg-[#00ff66]/10 text-white border-[#00ff66]/40'
                : 'text-zinc-400 border-white/10 hover:text-white hover:border-white/20'}`}
          >
            {f.label}
            <span className="ml-2 text-zinc-500">{filter === f.id ? counts[f.id] : ''}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          className="input-tech pl-9"
          placeholder="Search by trader, email, plan or cert ID"
          value={q}
          onChange={e => setQ(e.target.value)}
          data-testid="cert-search-input"
        />
      </div>

      {err && <div className="text-sm text-[#ff3366]" data-testid="cert-error">{err}</div>}

      {loading ? (
        <div className="card-tech rounded-lg p-10 text-center text-zinc-500 text-sm">Loading certificates…</div>
      ) : filtered.length === 0 ? (
        <div className="card-tech rounded-lg p-12 text-center" data-testid="cert-empty-state">
          <Award size={30} className="text-[#00ff66] mx-auto" />
          <h3 className="font-display text-2xl mt-3">No certificates {q ? 'match your search' : 'yet'}</h3>
          <p className="text-zinc-500 text-sm mt-1">
            {q ? 'Try a different search term.' : 'Approved payouts and phase upgrades issue certificates here automatically.'}
          </p>
        </div>
      ) : (
        <div className="card-tech rounded-lg overflow-x-auto">
          <div className="min-w-[1280px]">
            <div className="grid grid-cols-[1.4fr_1.6fr_1.2fr_0.9fr_0.9fr_0.9fr_0.9fr_0.7fr_1.5fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
              <div>CERTIFICATE</div>
              <div>TRADER</div>
              <div>PLAN</div>
              <div>SIZE</div>
              <div>AMOUNT</div>
              <div>ISSUED</div>
              <div>SHOWCASE</div>
              <div>IMAGE</div>
              <div className="text-right">ACTIONS</div>
            </div>
            {filtered.map(c => (
              <CertRow
                key={c.certificate_id}
                cert={c}
                downloading={downloading === c.certificate_id}
                uploading={uploadingId === c.certificate_id}
                onDownload={() => download(c.certificate_id)}
                onTogglePublic={() => togglePublic(c)}
                onUpload={(file) => uploadImage(c, file)}
                onClearImage={() => clearImage(c)}
                onDeleteShowcase={() => deleteShowcase(c)}
              />
            ))}
          </div>
        </div>
      )}

      {showcaseModal && (
        <ShowcaseCertModal
          onClose={() => setShowcaseModal(false)}
          onCreated={() => { setShowcaseModal(false); load(); }}
        />
      )}
    </div>
  );
}

function CertRow({ cert, downloading, uploading, onDownload, onTogglePublic, onUpload, onClearImage, onDeleteShowcase }) {
  const meta = TYPE_META[cert.cert_type] || TYPE_META.payout;
  const Icon = meta.icon;
  const fileRef = useRef(null);

  return (
    <div
      className="grid grid-cols-[1.4fr_1.6fr_1.2fr_0.9fr_0.9fr_0.9fr_0.9fr_0.7fr_1.5fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center hover:bg-white/[0.02] transition"
      data-testid={`admin-cert-row-${cert.certificate_id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Icon size={13} style={{ color: meta.tint }} />
          <span className="label-xs">{meta.label}</span>
          {cert.showcase_only && <span className="label-xs text-[#33ff85]">· SHOWCASE</span>}
        </div>
        <div className="font-mono text-[11px] text-zinc-500 mt-1 truncate">{cert.certificate_id}</div>
      </div>
      <div className="min-w-0">
        <div className="truncate">{cert.user_name || cert.trader_name || 'Unknown'}</div>
        <div className="text-xs text-zinc-500 truncate">{cert.user_email || (cert.showcase_only ? '(no user — showcase only)' : '—')}</div>
      </div>
      <div className="truncate text-zinc-300">{cert.plan_name || '—'}</div>
      <div className="font-mono text-zinc-300">{cert.account_size ? `$${Number(cert.account_size).toLocaleString()}` : '—'}</div>
      <div className="font-mono">
        {cert.cert_type === 'payout' && cert.amount != null
          ? <span className="text-[#00ff66]">${formatMoney(cert.amount)}</span>
          : <span className="text-zinc-500">—</span>}
      </div>
      <div className="text-xs text-zinc-400">{cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : '—'}</div>
      <div>
        <button
          onClick={onTogglePublic}
          data-testid={`toggle-public-${cert.certificate_id}`}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest border transition
            ${cert.is_public
              ? 'bg-[#00ff66]/10 text-[#00ff66] border-[#00ff66]/40 hover:bg-[#00ff66]/15'
              : 'text-zinc-500 border-white/10 hover:text-white hover:border-white/20'}`}
        >
          {cert.is_public ? <Eye size={11} /> : <EyeOff size={11} />}
          {cert.is_public ? 'PUBLIC' : 'PRIVATE'}
        </button>
      </div>
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }}
          data-testid={`file-input-${cert.certificate_id}`}
        />
        {cert.has_image ? (
          <button
            onClick={onClearImage}
            disabled={uploading}
            title="Remove image"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest text-[#33ff85] border border-[#33ff85]/40 hover:bg-[#33ff85]/10 disabled:opacity-50"
            data-testid={`clear-image-${cert.certificate_id}`}
          >
            <ImageIcon size={11} />
            {uploading ? '…' : 'SET'}
          </button>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Upload image"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-white/10 hover:text-white hover:border-white/20 disabled:opacity-50"
            data-testid={`upload-image-${cert.certificate_id}`}
          >
            <Upload size={11} />
            {uploading ? '…' : 'UPLOAD'}
          </button>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        {!cert.showcase_only && (
          <a
            href={`/cert/${cert.certificate_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost btn-sm"
            data-testid={`view-cert-${cert.certificate_id}`}
          >
            <ExternalLink size={12} className="inline mr-1" /> View
          </a>
        )}
        {cert.showcase_only ? (
          <button
            className="btn-ghost btn-sm text-[#ff3366]"
            onClick={onDeleteShowcase}
            data-testid={`delete-cert-${cert.certificate_id}`}
          >
            <Trash2 size={12} className="inline mr-1" /> Delete
          </button>
        ) : (
          <button
            className="btn-primary btn-sm"
            onClick={onDownload}
            disabled={downloading}
            data-testid={`download-cert-${cert.certificate_id}`}
          >
            <Download size={12} className="inline mr-1" />
            {downloading ? 'Downloading…' : 'PDF'}
          </button>
        )}
      </div>
    </div>
  );
}

function ShowcaseCertModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    trader_name: '',
    plan_name: 'One-Step Program',
    account_size: 10000,
    amount: '',
    cert_type: 'payout',
    display_order: 0,
    is_public: true,
  });
  const [preview, setPreview] = useState(null);   // data-url
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const pickFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { setErr('Only image files are supported.'); return; }
    if (f.size > MAX_UPLOAD_BYTES) { setErr('Image too large — max 6 MB.'); return; }
    setErr('');
    setFile(f);
    setPreview(await readFileAsDataURL(f));
  };

  const submit = async () => {
    setErr('');
    if (!file || !preview) { setErr('Please upload an image first.'); return; }
    if (!form.trader_name.trim()) { setErr('Trader name is required.'); return; }
    setBusy(true);
    try {
      await adminAPI.createShowcaseCertificate({
        image_base64: preview,
        trader_name: form.trader_name.trim(),
        plan_name: form.plan_name || null,
        account_size: form.account_size ? Number(form.account_size) : null,
        amount: form.amount === '' ? null : Number(form.amount),
        cert_type: form.cert_type,
        display_order: Number(form.display_order || 0),
        is_public: !!form.is_public,
      });
      onCreated();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="showcase-cert-modal">
      <div className="card-tech rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-sm">Wall of Consistency</div>
            <h2 className="font-display text-2xl mt-1">Upload showcase certificate</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Add a legacy or curated certificate image directly to the homepage marquee.
              No user is created — this cert exists purely for the showcase.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="showcase-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <div className="label-xs mb-2">CERTIFICATE IMAGE</div>
            {preview ? (
              <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black">
                <img src={preview} alt="preview" className="w-full max-h-72 object-contain" />
                <button
                  onClick={() => { setPreview(null); setFile(null); }}
                  className="absolute top-2 right-2 p-1 rounded-md bg-black/70 border border-white/10 hover:bg-black"
                  data-testid="showcase-clear-preview"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="block cursor-pointer" data-testid="showcase-file-drop">
                <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center hover:border-white/30 transition">
                  <Upload size={22} className="mx-auto text-zinc-500" />
                  <div className="text-sm text-zinc-400 mt-2">Click to upload — PNG, JPG or WebP, max 6 MB</div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => pickFile(e.target.files?.[0])}
                  data-testid="showcase-file-input"
                />
              </label>
            )}
          </div>

          <Field label="Trader name *">
            <input className="input-tech" value={form.trader_name}
                   onChange={e => setForm({ ...form, trader_name: e.target.value })}
                   data-testid="showcase-trader-name" />
          </Field>
          <Field label="Certificate type">
            <select className="input-tech" value={form.cert_type}
                    onChange={e => setForm({ ...form, cert_type: e.target.value })}
                    data-testid="showcase-cert-type">
              <option value="payout">Performance Reward (payout)</option>
              <option value="funded">Performance Trader (funded)</option>
              <option value="phase_1_passed">Phase 1 Passed</option>
              <option value="phase_2_passed">Phase 2 Passed</option>
            </select>
          </Field>
          <Field label="Plan / program label">
            <input className="input-tech" value={form.plan_name}
                   onChange={e => setForm({ ...form, plan_name: e.target.value })}
                   data-testid="showcase-plan-name" />
          </Field>
          <Field label="Account size (USD)">
            <input type="number" className="input-tech" value={form.account_size}
                   onChange={e => setForm({ ...form, account_size: e.target.value })}
                   data-testid="showcase-account-size" />
          </Field>
          <Field label="Reward amount (USD, optional)">
            <input type="number" step="0.01" className="input-tech" value={form.amount}
                   onChange={e => setForm({ ...form, amount: e.target.value })}
                   placeholder="Only for payout type"
                   data-testid="showcase-amount" />
          </Field>
          <Field label="Display order (lower = earlier)">
            <input type="number" className="input-tech" value={form.display_order}
                   onChange={e => setForm({ ...form, display_order: e.target.value })}
                   data-testid="showcase-display-order" />
          </Field>
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-zinc-400 mt-1">
            <input type="checkbox" checked={form.is_public}
                   onChange={e => setForm({ ...form, is_public: e.target.checked })}
                   data-testid="showcase-is-public" />
            Publish immediately on the Wall of Consistency
          </label>
        </div>

        {err && <div className="mt-4 text-sm text-[#ff3366]" data-testid="showcase-error">{err}</div>}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost btn-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="showcase-submit-btn">
            {busy ? 'Uploading…' : 'Publish to showcase'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-xs mb-2">{label}</div>
      {children}
    </div>
  );
}
